import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Sparkles, 
  Heart, 
  MessageCircle, 
  BookOpen, 
  ChevronRight,
  Send,
  PenLine,
  Quote,
  Loader2,
  Bookmark,
  Share2,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MessageSquare,
  Copy,
  Check,
  Download,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import {
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  EmailShareButton
} from 'react-share';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SoulFoodWidget({ user }) {
  const [soulFood, setSoulFood] = useState(null);
  const [reflections, setReflections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [reflectionResponse, setReflectionResponse] = useState('');
  const [savingToJournal, setSavingToJournal] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [soulFoodRes, reflectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/soul-food/current`, { headers }),
        axios.get(`${API_URL}/api/weekly-reflections/current`)
      ]);
      
      setSoulFood(soulFoodRes.data.soul_food);
      setReflections(reflectionsRes.data.reflections);
      
      // Set like and favorite states from API response
      if (soulFoodRes.data.soul_food) {
        setLiked(soulFoodRes.data.soul_food.is_liked || false);
        setFavorited(soulFoodRes.data.soul_food.is_favorited || false);
      }
    } catch (error) {
      console.error('Error fetching soul food:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!soulFood) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/soul-food/${soulFood.id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiked(response.data.liked);
      
      // Update local state
      setSoulFood(prev => ({
        ...prev,
        likes: response.data.liked 
          ? [...(prev.likes || []), user?.id]
          : (prev.likes || []).filter(id => id !== user?.id)
      }));
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleFavorite = async () => {
    if (!soulFood) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/soul-food/${soulFood.id}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFavorited(response.data.favorited);
      
      if (response.data.favorited) {
        toast.success('Added to your favorites! 🔖');
      } else {
        toast.success('Removed from favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleComment = async () => {
    if (!newComment.trim() || !soulFood) return;
    
    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/soul-food/${soulFood.id}/comment`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSoulFood(prev => ({
        ...prev,
        comments: [...(prev.comments || []), response.data.comment]
      }));
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSaveToJournal = async (prompt) => {
    if (!reflectionResponse.trim()) {
      toast.error('Please write your reflection first');
      return;
    }
    
    setSavingToJournal(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/weekly-reflections/${prompt.id}/to-journal`,
        {
          title: `Reflection: ${prompt.theme}`,
          content: reflectionResponse,
          prompt: prompt.prompt,
          tags: [prompt.theme, prompt.category]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Saved to your journal! +5 diamond points ✨');
      setSelectedReflection(null);
      setReflectionResponse('');
    } catch (error) {
      toast.error('Failed to save to journal');
    } finally {
      setSavingToJournal(false);
    }
  };

  const handleOpenShare = async () => {
    if (!soulFood) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${API_URL}/api/soul-food/${soulFood.id}/share-data`,
        { headers }
      );
      
      setShareData(response.data);
      setShowShareModal(true);
    } catch (error) {
      toast.error('Failed to load share options');
    }
  };

  const handleCopyLink = async () => {
    if (!shareData) return;
    
    try {
      await navigator.clipboard.writeText(shareData.share_url);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      
      // Track the share
      trackShare('copy_link');
      
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleDownloadImage = async () => {
    if (!soulFood) return;
    
    setDownloadingImage(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/soul-food/${soulFood.id}/share-image`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `soul-food-${soulFood.id}.png`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Image downloaded!');
      trackShare('download_image');
    } catch (error) {
      toast.error('Failed to download image');
    } finally {
      setDownloadingImage(false);
    }
  };

  const trackShare = async (platform) => {
    if (!soulFood) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API_URL}/api/soul-food/${soulFood.id}/track-share`,
        { platform },
        { headers }
      );
    } catch (error) {
      // Silent fail for tracking
      console.error('Failed to track share:', error);
    }
  };

  const getShareText = () => {
    if (!soulFood) return '';
    return `"${soulFood.quote_or_scripture}"\n— ${soulFood.source}\n\n#SisterDiamonds #${soulFood.theme?.replace(/\s+/g, '') || 'Inspiration'}`;
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-100">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Soul Food Card */}
      {soulFood && (
        <Card className="border-2 border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 overflow-hidden" data-testid="soul-food-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading text-purple-900">Soul Food</CardTitle>
                  <CardDescription className="text-xs text-purple-600">This Week's Inspiration</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {soulFood.theme && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                    {soulFood.theme}
                  </Badge>
                )}
                <Link to="/favorites/soul-food">
                  <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-800" data-testid="view-favorites-link">
                    <Bookmark className="h-4 w-4 mr-1" />
                    My Saved
                  </Button>
                </Link>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-0">
            {/* Quote/Scripture */}
            <div className="relative my-4 px-4">
              <Quote className="absolute -top-2 -left-1 h-6 w-6 text-purple-200" />
              <p className="text-lg italic text-gray-800 leading-relaxed pl-4">
                "{soulFood.quote_or_scripture}"
              </p>
              <p className="text-sm text-purple-600 font-medium mt-2 pl-4">
                — {soulFood.source}
              </p>
            </div>
            
            {/* Reflection */}
            <p className="text-gray-700 mt-4 leading-relaxed">
              {soulFood.reflection}
            </p>
            
            {/* Actions */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-purple-100">
              <Button
                variant="ghost"
                size="sm"
                className={`gap-1 ${liked ? 'text-pink-500' : 'text-gray-500'}`}
                onClick={handleLike}
                data-testid="soul-food-like-btn"
              >
                <Heart className={`h-4 w-4 ${liked ? 'fill-current' : ''}`} />
                {soulFood.likes?.length || 0}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-gray-500"
                onClick={() => setShowComments(!showComments)}
                data-testid="soul-food-comments-btn"
              >
                <MessageCircle className="h-4 w-4" />
                {soulFood.comments?.length || 0}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className={`gap-1 ${favorited ? 'text-amber-500' : 'text-gray-500'}`}
                onClick={handleFavorite}
                data-testid="soul-food-favorite-btn"
              >
                <Bookmark className={`h-4 w-4 ${favorited ? 'fill-current' : ''}`} />
                {favorited ? 'Saved' : 'Save'}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 ml-auto text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                onClick={handleOpenShare}
                data-testid="soul-food-share-btn"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            </div>
            
            {/* Comments Section */}
            {showComments && (
              <div className="mt-4 space-y-3">
                {soulFood.comments?.map((comment) => (
                  <div key={comment.id} className="flex gap-2 p-2 bg-white/50 rounded-lg">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={comment.user_image} />
                      <AvatarFallback className="text-xs bg-purple-100 text-purple-600">
                        {comment.user_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{comment.user_name}</p>
                      <p className="text-sm text-gray-600">{comment.content}</p>
                    </div>
                  </div>
                ))}
                
                {/* Add Comment */}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Share your thoughts..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1"
                    data-testid="soul-food-comment-input"
                  />
                  <Button 
                    size="sm" 
                    onClick={handleComment}
                    disabled={submittingComment || !newComment.trim()}
                    data-testid="soul-food-comment-submit"
                  >
                    {submittingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Weekly Reflections Card */}
      {reflections && reflections.prompts?.length > 0 && (
        <Card className="border-2 border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="weekly-reflections-card">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading text-amber-900">Weekly Reflections</CardTitle>
                <CardDescription className="text-xs text-amber-600">Prompts for Your Journey</CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-0 space-y-3">
            {reflections.prompts.map((prompt, index) => (
              <div 
                key={prompt.id} 
                className={`p-3 rounded-lg transition-all ${
                  selectedReflection?.id === prompt.id 
                    ? 'bg-white shadow-sm border border-amber-200' 
                    : 'bg-white/50 hover:bg-white/80 cursor-pointer'
                }`}
                onClick={() => selectedReflection?.id !== prompt.id && setSelectedReflection(prompt)}
                data-testid={`reflection-prompt-${index}`}
              >
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="shrink-0 text-amber-600 border-amber-300">
                    {prompt.theme}
                  </Badge>
                  <p className="text-sm text-gray-800 font-medium">{prompt.prompt}</p>
                </div>
                
                {prompt.follow_up && selectedReflection?.id !== prompt.id && (
                  <p className="text-xs text-gray-500 mt-1 ml-16">{prompt.follow_up}</p>
                )}
                
                {/* Expanded View for Selected Prompt */}
                {selectedReflection?.id === prompt.id && (
                  <div className="mt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    {prompt.follow_up && (
                      <p className="text-sm text-amber-700 italic">{prompt.follow_up}</p>
                    )}
                    
                    <Textarea
                      placeholder="Write your reflection..."
                      value={reflectionResponse}
                      onChange={(e) => setReflectionResponse(e.target.value)}
                      className="min-h-[100px] bg-white"
                      data-testid="reflection-response-input"
                    />
                    
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedReflection(null);
                          setReflectionResponse('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600"
                        onClick={() => handleSaveToJournal(prompt)}
                        disabled={savingToJournal}
                        data-testid="save-to-journal-btn"
                      >
                        {savingToJournal ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <PenLine className="h-4 w-4 mr-1" />
                        )}
                        Save to Journal
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No content message */}
      {!soulFood && !reflections && (
        <Card className="border-2 border-gray-100">
          <CardContent className="p-6 text-center text-gray-500">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>Soul Food is being prepared for this week...</p>
            <p className="text-sm">Check back Sunday morning!</p>
          </CardContent>
        </Card>
      )}

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-purple-600" />
              Share Soul Food
            </DialogTitle>
            <DialogDescription>
              Spread the inspiration with your community
            </DialogDescription>
          </DialogHeader>

          {soulFood && (
            <div className="space-y-6">
              {/* Preview Card */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200">
                <p className="text-sm italic text-gray-800 line-clamp-3">
                  "{soulFood.quote_or_scripture}"
                </p>
                <p className="text-xs text-purple-600 mt-1">— {soulFood.source}</p>
              </div>

              {/* Step 1: Download Image for Instagram */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">1</span>
                  <p className="text-sm font-medium text-gray-700">Download the image (for Instagram)</p>
                </div>
                <button
                  onClick={handleDownloadImage}
                  disabled={downloadingImage}
                  className="w-full"
                  data-testid="share-download-image-main"
                >
                  <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transition-colors cursor-pointer">
                    {downloadingImage ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Download className="h-5 w-5" />
                    )}
                    <span className="font-medium">Download Share Image</span>
                  </div>
                </button>
              </div>

              {/* Step 2: Share on Social Media */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold">2</span>
                  <p className="text-sm font-medium text-gray-700">Share on your favorite platform</p>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <a
                    href="https://www.instagram.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackShare('instagram')}
                    className="w-full"
                    title="Open Instagram to share your downloaded image"
                  >
                    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-colors cursor-pointer border-2 border-pink-200" data-testid="share-instagram">
                      <Instagram className="h-6 w-6 text-pink-600" />
                      <span className="text-xs text-pink-700 font-medium">Instagram</span>
                    </div>
                  </a>

                  <FacebookShareButton
                    url={shareData?.share_url || ''}
                    quote={getShareText()}
                    onClick={() => trackShare('facebook')}
                    className="w-full"
                  >
                    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer" data-testid="share-facebook">
                      <Facebook className="h-6 w-6 text-blue-600" />
                      <span className="text-xs text-blue-700">Facebook</span>
                    </div>
                  </FacebookShareButton>

                  <LinkedinShareButton
                    url={shareData?.share_url || ''}
                    title="Soul Food from Sister Diamonds"
                    summary={getShareText()}
                    onClick={() => trackShare('linkedin')}
                    className="w-full"
                  >
                    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer" data-testid="share-linkedin">
                      <Linkedin className="h-6 w-6 text-blue-700" />
                      <span className="text-xs text-blue-800">LinkedIn</span>
                    </div>
                  </LinkedinShareButton>

                  <WhatsappShareButton
                    url={shareData?.share_url || ''}
                    title={getShareText()}
                    onClick={() => trackShare('whatsapp')}
                    className="w-full"
                  >
                    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors cursor-pointer" data-testid="share-whatsapp">
                      <MessageSquare className="h-6 w-6 text-green-600" />
                      <span className="text-xs text-green-700">WhatsApp</span>
                    </div>
                  </WhatsappShareButton>
                </div>

                {/* More options row */}
                <div className="grid grid-cols-2 gap-3">
                  <EmailShareButton
                    url={shareData?.share_url || ''}
                    subject="Soul Food from Sister Diamonds"
                    body={getShareText()}
                    onClick={() => trackShare('email')}
                    className="w-full"
                  >
                    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-testid="share-email">
                      <Mail className="h-6 w-6 text-gray-600" />
                      <span className="text-xs text-gray-700">Email</span>
                    </div>
                  </EmailShareButton>

                  <button
                    onClick={handleCopyLink}
                    className="w-full"
                    data-testid="share-copy-link"
                  >
                    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors cursor-pointer">
                      {copiedLink ? (
                        <Check className="h-6 w-6 text-green-600" />
                      ) : (
                        <Copy className="h-6 w-6 text-purple-600" />
                      )}
                      <span className="text-xs text-purple-700">
                        {copiedLink ? 'Copied!' : 'Copy Link'}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Tip for Instagram */}
              <div className="p-3 bg-pink-50 rounded-lg border border-pink-100">
                <p className="text-xs text-pink-700 text-center">
                  <strong>Tip for Instagram:</strong> Download the image first, then open Instagram and create a new post with the downloaded image.
                </p>
              </div>

              {/* Branding Note */}
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 text-center">
                  When you share, you're helping spread the Sister Diamonds mission to empower extraordinary women everywhere.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
